// @ts-ignore — @types/actioncable exports Consumer from the root
import type { Consumer } from "@rails/actioncable";
import { Emitter, type Disposer } from "./Emitter";
import { initialState, type LastBotState } from "./state";
import type {
  AnyCoreEventHandler,
  CoreEventHandler,
  CoreEventName,
  CoreEvents,
} from "./events";
import type {
  Contact,
  CoreConfig,
  Message,
  MessageThread,
  Task,
  User,
  VoiceSession,
} from "./types";
import { createWidgetToken } from "./services/widgetTokenService";
import { getWidgetById } from "./services/widgetService";
import {
  createLoginCode,
  verifyLoginCode,
} from "./services/loginCodeService";
import { createCableConsumer } from "./channels/cable";
import { AccountChannel } from "./channels/accountChannel";
import {
  MessageThreadChannel,
  TypingState,
  type TypingNotification,
} from "./channels/messageThreadChannel";
import {
  createMessageThread,
  getLinkTrackingUrl,
  getMessageThread,
  getMessageThreads,
} from "./services/messageThreadService";
import { uploadFile } from "./services/fileUploadService";
import type { ErrorReason } from "./services/fileUploadService";
import { createVoiceSession } from "./services/voiceSessionService";
import { getSearchSuggestions } from "./services/searchSuggestionService";
import storageService from "./services/storageService";
import {
  SLOT_REGISTRY,
  type SlotDescriptor,
} from "../templating/slotRegistry";
import { isObject } from "../utils/isObject";

/**
 * Versioning: the integer part is the breaking-surface generation (bump it
 * only for breaking changes); the fractional part counts additive,
 * backward-compatible additions within that generation. Embedders
 * feature-detect with `core.apiVersion >= 2.1`. Because this is a plain JS
 * number, the minor part must never exceed 9 (`2.10 === 2.1`).
 *
 * v3.1 adds (LAS-4711):
 *   - `state.widget.attachmentsEnabled`: false when the account has visitor
 *     attachments switched off; the default UI then hides its attach button.
 *
 * v3 introduces (LAS-2166):
 *   - BREAKING: `startLogin()` / `openHistory()` / `openWelcome()` are
 *     removed, consolidated into one `showPage("landing" | "history" |
 *     "login", { returnTo? })`. The `nav:*` events are unchanged.
 *   - Promise waiting API: `when(predicate)` (the primitive — late-subscriber
 *     safe, checks current state first), `waitFor(event)` (one-shot event
 *     wait with `timeoutMs`/`AbortSignal`), and `open()` now returns a
 *     `Promise<void>` that resolves once the panel is mounted and interactive
 *     (immediately in headless mode / when already open).
 *   - New state slice: `state.ui.panelInteractive`.
 *   - Pending waits reject with `CoreWaitError`
 *     (`reason: 'destroyed' | 'timeout' | 'aborted'`).
 *
 * v2 introduced:
 *   - New action methods: `toggleFullscreen()`, `startLogin()`, `openHistory()`.
 *   - New events: `widget:fullscreen-toggle`, `nav:login`, `nav:history`, `nav:landing`.
 *   - Slot registry with globally-unique ids (breaking: `logo` → `brand-logo`,
 *     `title` → `form-title`, etc.) and DOM event dispatch via the
 *     `{slot}:{action}` contract.
 *   - `core.getHostElement()` for embedder integration.
 *
 * See docs/api.md and LAS-1464 for the full migration guide.
 */
export const API_VERSION = 3.1;

/**
 * Machine-readable cause carried by a rejected wait (`waitFor` / `when` /
 * `open`):
 *   - `destroyed` — `core.destroy()` ran while the wait was pending.
 *   - `timeout` — `waitFor`'s `timeoutMs` elapsed first.
 *   - `aborted` — the supplied `AbortSignal` fired.
 */
export type WaitRejectReason = "destroyed" | "timeout" | "aborted";

/**
 * Pages the default UI can be navigated to via `showPage()`. The
 * conversation page is intentionally absent — it requires thread context,
 * which `openThread()` / `sendMessage()` / `startNewThread()` establish.
 */
export type NavigablePage = "landing" | "history" | "login";

/**
 * Rejection error for the promise waiting API. Branch on `reason`:
 *
 *   try {
 *     await core.waitFor("thread:opened", { timeoutMs: 5000 });
 *   } catch (err) {
 *     if (err instanceof CoreWaitError && err.reason === "timeout") { ... }
 *   }
 */
export class CoreWaitError extends Error {
  readonly reason: WaitRejectReason;

  constructor(reason: WaitRejectReason, message: string) {
    super(message);
    this.name = "CoreWaitError";
    this.reason = reason;
  }
}

type StateHandler = (_state: LastBotState) => void;

const getNotImplementedError = (method: string): Error =>
  new Error(`LastBotCore.${method} is not implemented yet`);

const getApiError = (method: string, status: number | undefined): Error =>
  new Error(
    `${method} failed${status ? ` (status ${status})` : ""}`,
    { cause: { status } },
  );

export interface InitResult {
  success: boolean;
  status?: number;
}

export class LastBotCore {
  static readonly apiVersion = API_VERSION;

  readonly apiVersion = API_VERSION;
  readonly config: Readonly<CoreConfig>;
  readonly apiUrl: string;

  private emitter = new Emitter();
  // Every valid public setSize call advances this revision before the synchronous
  // event emission. The default UI uses it to notice an embedder call that landed
  // before React had installed its widget:set-size listener.
  private sizeRevision = 0;
  private state: LastBotState = initialState();
  private stateHandlers = new Set<StateHandler>();
  private destroyed = false;
  private initPromise: Promise<InitResult> | null = null;
  private abortController = new AbortController();
  private token: string | null = null;
  private loginEmail: string | null = null;
  private consumer: Consumer | null = null;
  private accountChannel: AccountChannel | null = null;
  private messageThreadChannel: MessageThreadChannel | null = null;
  /**
   * Reference to the `<lastbot-chat>` custom element this core is attached
   * to, when one exists. Set by `widget.tsx`'s `connectedCallback`. Used by
   * the v2 slot-event dispatcher to fire CustomEvents at the host element
   * where embedders register their own listeners (`element.addEventListener
   * ("login:click", …)`). Null in headless mode — embedders own the UI and
   * don't need DOM event dispatch.
   */
  private hostElement: HTMLElement | null = null;
  /**
   * True for `<lastbot-chat headless="true">` placements. The host element
   * IS set in that mode (slot-event dispatch and `getCore()` liveness checks
   * need it) but the React renderer is skipped, so no default UI will ever
   * ack panel readiness — `open()` must resolve immediately instead of
   * waiting on `state.ui.panelInteractive`. Pure-core `LastBot.create()`
   * embedders are covered separately by `hostElement === null`.
   */
  private headless = false;
  /**
   * Reject callbacks for waits (`waitFor` / `when` / `open`) that are still
   * pending. `destroy()` drains this set so an SPA unmount never leaves a
   * hanging `await` — each entry settles its promise with a `CoreWaitError`
   * (`reason: 'destroyed'`) and runs that wait's cleanup.
   */
  private pendingWaitRejects = new Set<(_error: CoreWaitError) => void>();
  /**
   * Last contents sent via the channel send path (existing-thread fast path).
   * Cleared when the corresponding `message_created` frame echoes back or when
   * an error is surfaced. Single-slot — assumes a single in-flight message,
   * which matches the widget's UX.
   */
  private lastChannelMessage: string | null = null;

  constructor(config: CoreConfig) {
    this.config = Object.freeze({ ...config });
    this.apiUrl = `${config.baseUrl}/api/v1`;
  }

  /**
   * True once `destroy()` has run. A destroyed core rejects further calls
   * (`assertAlive`); consumers holding a reference (e.g. via
   * `window.LastBot.getCore`) can check this to avoid using a torn-down instance.
   */
  public get isDestroyed(): boolean {
    return this.destroyed;
  }

  init(): Promise<InitResult> {
    this.assertAlive();
    if (this.initPromise) return this.initPromise;
    this.initPromise = this.runInit()
      .then(result => {
        this.maybeDispatchLifecycleEvent(result);
        return result;
      })
      .catch(err => {
        // `runInit` currently never rejects — all known failure paths
        // return `{ success: false, status }` cleanly. The catch is a
        // defensive guard so an unexpected throw from a future internal
        // change (e.g. a service helper that starts throwing instead of
        // returning a result) still dispatches `lastbot:error` on window,
        // matching the headless contract. The error is re-thrown so the
        // caller's `await core.init()` still rejects.
        this.maybeDispatchLifecycleEvent({ success: false });
        throw err;
      });
    return this.initPromise;
  }

  /**
   * Headless-mode auto-dispatch of the `lastbot:ready` / `lastbot:error`
   * CustomEvent on `window`. A core attached to a `<lastbot-chat>` custom
   * element has its host element set via `_setHostElement` before init —
   * in that mode the host handles dispatch (and waits for stylesheet
   * injection before firing). When `hostElement` is null we're alone:
   * dispatch ourselves so listeners attached to `window` get the same
   * lifecycle signal as they would in custom-element mode.
   *
   * Skipped if destroy() ran during init (no signal for a torn-down core)
   * or if `window` is unavailable (Node test environments).
   */
  private maybeDispatchLifecycleEvent(result: InitResult): void {
    if (this.hostElement !== null) return;
    if (this.destroyed) return;
    if (typeof window === "undefined") return;
    // Mirror the custom-element dispatch: expose widgetId/placementId so window-level
    // listeners can discriminate placements without reaching into detail.core.
    const ids = {
      widgetId: this.config.widgetId,
      placementId: this.config.placementId,
    };
    const detail = result.success
      ? { core: this, ...ids }
      : { core: this, status: result.status, ...ids };
    window.dispatchEvent(
      new CustomEvent(result.success ? "lastbot:ready" : "lastbot:error", {
        detail,
      }),
    );
  }

  private async runInit(): Promise<InitResult> {
    const signal = this.abortController.signal;

    const tokenResult = await createWidgetToken(this.apiUrl, signal);
    if (this.destroyed) return { success: false };
    if (!tokenResult.success) {
      this.setConnectionStatus("error");
      return { success: false, status: tokenResult.status };
    }
    this.token = tokenResult.widgetToken.token;

    const widgetResult = await getWidgetById(
      this.apiUrl,
      this.token,
      this.config.widgetId,
      this.config.userEmail ?? "",
      signal,
    );
    if (this.destroyed) return { success: false };
    if (!widgetResult.success) {
      this.setConnectionStatus("error");
      return { success: false, status: widgetResult.status };
    }

    // Persist the visitor contact returned by the widget config so reloads identify the same
    // visitor.
    storageService.setContact(this.config.widgetId, widgetResult.widget.contact);
    this._setState(s => ({
      ...s,
      brand: widgetResult.widget.brand_information,
      translations: widgetResult.widget.translations,
      contact: widgetResult.widget.contact,
      agents: {
        ...s.agents,
        online: widgetResult.widget.support_agents,
      },
      widget: {
        voiceModeAvailable: widgetResult.widget.voice_mode_available,
        // Only an explicit `false` disables attachments: an older server sends
        // no field, and its widgets keep the attach button (LAS-4711).
        attachmentsEnabled: widgetResult.widget.attachments_enabled !== false,
        conversationStarters: widgetResult.widget.conversation_starters,
        averageResponseTime: widgetResult.widget.average_response_time,
        theme: widgetResult.widget.theme,
      },
    }));

    this.connectAccountChannel(this.token);

    return { success: true };
  }

  private connectAccountChannel(token: string): void {
    this.consumer = createCableConsumer(this.config.baseUrl, token);
    this.accountChannel = new AccountChannel(this.consumer);
    this.accountChannel.subscribe({
      onConnected: () => this.setConnectionStatus("open"),
      onDisconnected: () => this.setConnectionStatus("closed"),
      onRejected: () => this.setConnectionStatus("error"),
      onAccountUserCreated: ({ accountUser }) =>
        this.handleAccountUserCreated(accountUser),
      onAccountUserUpdated: ({ accountUser }) =>
        this.handleAccountUserUpdated(accountUser),
      onAccountUserDestroyed: ({ accountUser }) =>
        this.handleAccountUserDestroyed(accountUser),
      onError: ({ status }) =>
        this.emitter.emit("error", {
          code: "account.channel-error",
          message: `Account channel error (status ${status})`,
          details: { status },
        }),
    });
  }

  private handleAccountUserCreated(user: User): void {
    this._setState(s => ({
      ...s,
      agents: { ...s.agents, online: [...s.agents.online, user] },
    }));
    this.emitter.emit("agent:joined", user);
    this.emitter.emit("agents:online", { users: this.state.agents.online });
  }

  private handleAccountUserUpdated(user: User): void {
    this._setState(s => ({
      ...s,
      agents: {
        ...s.agents,
        online: s.agents.online.map(a =>
          a.id === user.id ? { ...a, ...user } : a,
        ),
      },
    }));
    this.emitter.emit("agents:online", { users: this.state.agents.online });
  }

  private handleAccountUserDestroyed(user: User): void {
    this._setState(s => ({
      ...s,
      agents: {
        ...s.agents,
        online: s.agents.online.filter(a => a.id !== user.id),
      },
    }));
    this.emitter.emit("agent:left", { id: user.id });
    this.emitter.emit("agents:online", { users: this.state.agents.online });
  }

  private setConnectionStatus(status: LastBotState["connectionStatus"]): void {
    if (this.state.connectionStatus === status) return;
    this._setState(s => ({ ...s, connectionStatus: status }));
    this.emitter.emit("connection:status", status);
  }

  on<K extends CoreEventName>(event: K, handler: CoreEventHandler<K>): Disposer {
    this.assertAlive();
    return this.emitter.on(event, handler);
  }

  off<K extends CoreEventName>(event: K, handler: CoreEventHandler<K>): void {
    this.emitter.off(event, handler);
  }

  onAny(handler: AnyCoreEventHandler): Disposer {
    this.assertAlive();
    return this.emitter.onAny(handler);
  }

  offAny(handler: AnyCoreEventHandler): void {
    this.emitter.offAny(handler);
  }

  getState(): LastBotState {
    return this.state;
  }

  /**
   * Shared mechanics for the promise waiting API: one-shot settle, cleanup of
   * every attached resource (source subscription, abort listener, timeout
   * timer, destroy registration) on whichever path settles first, and
   * registration in `pendingWaitRejects` so `destroy()` rejects the wait.
   *
   * `attachSource` subscribes to the thing being waited on and returns its
   * unsubscribe function. The resolve/reject it receives are settle-once:
   * calls after the first are ignored.
   */
  private createWait<T>(
    what: string,
    options: { signal?: AbortSignal; timeoutMs?: number } | undefined,
    attachSource: (
      _resolve: (_value: T) => void,
      _reject: (_error: unknown) => void,
    ) => () => void,
  ): Promise<T> {
    const signal = options?.signal;
    if (signal?.aborted) {
      return Promise.reject(
        new CoreWaitError("aborted", `${what} aborted before it started`),
      );
    }
    return new Promise<T>((resolve, reject) => {
      let settled = false;
      const cleanups: (() => void)[] = [];
      const settle = (deliver: () => void): void => {
        if (settled) return;
        settled = true;
        for (const cleanup of cleanups) cleanup();
        deliver();
      };
      const settleResolve = (value: T): void => settle(() => resolve(value));
      const settleReject = (error: unknown): void =>
        settle(() => reject(error));

      cleanups.push(attachSource(settleResolve, settleReject));

      this.pendingWaitRejects.add(settleReject);
      cleanups.push(() => this.pendingWaitRejects.delete(settleReject));

      if (signal) {
        const onAbort = (): void =>
          settleReject(new CoreWaitError("aborted", `${what} aborted`));
        signal.addEventListener("abort", onAbort, { once: true });
        cleanups.push(() => signal.removeEventListener("abort", onAbort));
      }
      if (options?.timeoutMs !== undefined) {
        const timer = setTimeout(() => {
          settleReject(
            new CoreWaitError(
              "timeout",
              `${what} timed out after ${options.timeoutMs}ms`,
            ),
          );
        }, options.timeoutMs);
        cleanups.push(() => clearTimeout(timer));
      }
    });
  }

  /**
   * Wait for the NEXT occurrence of an event. One-shot: the listener is
   * removed as soon as the promise settles, whichever way it settles.
   * Resolves with the event's payload (typed by the event map, mirroring
   * `on()`).
   *
   *   const { threadId } = await core.waitFor("thread:opened", { timeoutMs: 5000 });
   *
   * Rejects with `CoreWaitError` when `timeoutMs` elapses (`reason:
   * 'timeout'`), the `signal` fires (`'aborted'`), or the core is destroyed
   * while waiting (`'destroyed'`).
   *
   * NOTE — waiting on a FUTURE event has the same late-subscriber hazard as
   * `on()`: an event emitted before `waitFor` was called is not seen. For
   * conditions ("the panel is open", "a thread is active") use `when()`,
   * which checks current state first and cannot miss.
   */
  waitFor<K extends CoreEventName>(
    event: K,
    options?: { signal?: AbortSignal; timeoutMs?: number },
  ): Promise<CoreEvents[K]> {
    this.assertAlive();
    return this.createWait<CoreEvents[K]>(
      `waitFor("${event}")`,
      options,
      resolve => this.emitter.on(event, payload => resolve(payload)),
    );
  }

  /**
   * Wait until a state condition holds. Late-subscriber-safe: the predicate
   * is checked against CURRENT state first and the promise resolves
   * immediately if it already passes; otherwise it subscribes and resolves on
   * the first state change that satisfies it (auto-unsubscribing either way).
   * Resolves with the state snapshot that satisfied the predicate.
   *
   *   await core.when(s => s.ui.panelInteractive);
   *   core.sendMessage("Hello");
   *
   * Rejects with `CoreWaitError` when the `signal` fires (`reason:
   * 'aborted'`) or the core is destroyed while waiting (`'destroyed'`); a
   * predicate that throws rejects with its own error. Headless note:
   * predicates over `state.ui` flags never become true in headless mode (no
   * default UI ever mounts).
   */
  when(
    predicate: (_state: LastBotState) => boolean,
    options?: { signal?: AbortSignal },
  ): Promise<LastBotState> {
    this.assertAlive();
    // The initial check must reject (not throw) on a bad predicate so the
    // method never throws synchronously once it has decided to return a
    // promise — `core.when(p).catch(...)` has to catch every predicate error.
    try {
      if (!options?.signal?.aborted && predicate(this.state)) {
        return Promise.resolve(this.state);
      }
    } catch (error) {
      // Raw error, matching the subscribed-path rejection below.
      return Promise.reject(error);
    }
    return this.createWait<LastBotState>("when()", options, (resolve, reject) =>
      this.subscribe(state => {
        try {
          if (predicate(state)) resolve(state);
        } catch (error) {
          reject(error);
        }
      }),
    );
  }

  subscribe(handler: StateHandler): () => void {
    this.assertAlive();
    this.stateHandlers.add(handler);
    return () => {
      this.stateHandlers.delete(handler);
    };
  }

  /**
   * Vanilla-JS DOM binding helper for headless embedders. Subscribes to state, calls `selector`
   * to extract a slice, and invokes `render(element, value)` whenever the selected slice changes
   * (reference equality on the result of `selector`). Returns an unbind function.
   *
   * Calls `render` synchronously once on bind so the embedder doesn't need a separate initial
   * paint step.
   *
   *   const unbind = core.bind('.messages',
   *     s => s.thread?.messages ?? [],
   *     (el, messages) => { el.innerHTML = messages.map(renderRow).join(''); }
   *   );
   *
   * Throws synchronously if `target` is a selector string that resolves to no element.
   */
  bind<T>(
    target: Element | string,
    selector: (_state: LastBotState) => T,
    render: (_element: Element, _value: T) => void,
  ): () => void {
    this.assertAlive();
    const element =
      typeof target === "string" ? document.querySelector(target) : target;
    if (!element) {
      throw new Error(
        `LastBotCore.bind: selector "${String(target)}" did not match any element`,
      );
    }
    let lastValue = selector(this.state);
    render(element, lastValue);
    const unsubscribe = this.subscribe(state => {
      const next = selector(state);
      if (Object.is(next, lastValue)) return;
      lastValue = next;
      render(element, next);
    });
    return unsubscribe;
  }

  /**
   * Open the chat UI. Emits `widget:open`; the default React UI listens and
   * toggles its `isOpen` flag. Returns a promise that resolves once the panel
   * is mounted AND interactive (`state.ui.panelInteractive` — the default
   * UI's readiness ack), so nav commands issued after the await land:
   *
   *   await core.open();
   *   core.showPage("history");   // panel is mounted, nav listener attached — works
   *
   * Calling `showPage()` in the SAME tick as `open()` while the panel is
   * closed is unsupported — the `nav:*` event fires before the panel's
   * listener exists. Await `open()` first.
   *
   * Resolves immediately when the panel is already open, and in headless mode
   * (the embedder owns rendering — the event is informational and no UI will
   * ever mount). Rejects with `CoreWaitError` (`reason: 'destroyed'`) if the
   * core is destroyed while the panel is still mounting; fire-and-forget
   * callers (`core.open()` without await, the v2 style) are safe — the
   * rejection is pre-handled internally and never surfaces as an unhandled
   * promise rejection.
   */
  open(): Promise<void> {
    this.assertAlive();
    this.emitter.emit("widget:open", {});
    if (this.hostElement === null || this.headless) {
      return Promise.resolve();
    }
    let ready: Promise<void>;
    try {
      ready = this.when(s => s.ui.panelInteractive).then(
        (): void => undefined,
      );
    } catch {
      // A widget:open handler tore the core down during the synchronous emit
      // above, so when()'s assertAlive threw. Honor the promise contract —
      // reject like any other wait pending at destroy() instead of throwing
      // synchronously out of open().
      ready = Promise.reject(
        new CoreWaitError("destroyed", "LastBotCore was destroyed while waiting"),
      );
    }
    // Keep the v2 fire-and-forget calling style safe: attaching a no-op
    // rejection handler marks the rejection handled for callers that ignore
    // the return value, while `await core.open()` still rejects normally.
    ready.catch((): void => undefined);
    return ready;
  }

  /**
   * Returns the `<lastbot-chat>` element this core is attached to, or `null`
   * when in headless mode. Used by the slot-event dispatcher; rarely needed
   * by embedders directly (prefer `element.addEventListener` on the
   * `<lastbot-chat>` you already hold).
   */
  getHostElement(): HTMLElement | null {
    return this.hostElement;
  }

  /**
   * Returns a snapshot of every registered slot — id, kind, dispatched
   * event name (for interactive slots), and developer-facing purpose copy.
   * Use this for introspection in build tools, dev consoles, embedder
   * scaffolding, or runtime template validation.
   *
   * The returned array is a fresh copy; mutations don't affect the
   * registry. The registry itself is frozen at module load.
   */
  listSlots(): SlotDescriptor[] {
    this.assertAlive();
    return Object.values(SLOT_REGISTRY).map(d => ({ ...d }));
  }

  /** @internal — set by widget.tsx's connectedCallback. */
  _setHostElement(element: HTMLElement | null): void {
    this.hostElement = element;
  }

  /**
   * @internal — set by widget.tsx's connectedCallback for
   * `<lastbot-chat headless="true">` placements, where a host element exists
   * but no default UI ever mounts. Lets `open()` resolve immediately instead
   * of waiting for a panel-readiness ack that will never come.
   */
  _setHeadless(headless: boolean): void {
    this.headless = headless;
  }

  /**
   * @internal — default-UI readiness ack (LAS-2166). ChatWidget calls this
   * with `true` after its mount effects have flushed (so the `nav:*`
   * listeners are attached) and with `false` from its unmount cleanup.
   * Drives `state.ui.panelInteractive`, which `open()`'s promise and
   * embedder `when()` predicates wait on. Tolerates a destroyed core instead
   * of asserting: React unmount cleanup can run after `destroy()` during SPA
   * teardown.
   */
  _setPanelInteractive(interactive: boolean): void {
    if (this.destroyed) return;
    if (this.state.ui.panelInteractive === interactive) return;
    this._setState(s => ({
      ...s,
      ui: { ...s.ui, panelInteractive: interactive },
    }));
  }

  /**
   * Close the chat UI. Emits `widget:close`. Mirror of `open()`.
   */
  close(): void {
    this.assertAlive();
    this.emitter.emit("widget:close", {});
  }

  /**
   * Toggle the chat panel's fullscreen / docked mode. Emits
   * `widget:fullscreen-toggle`. The default UI flips its `isFullscreen` flag
   * in response; headless embedders can ignore.
   *
   * v2 action surface: lifted from a per-component handler so interactive
   * slots (e.g. the `fullscreen-button` slot) can call into core directly
   * via the registry's event contract.
   */
  toggleFullscreen(): void {
    this.assertAlive();
    this.emitter.emit("widget:fullscreen-toggle", {});
  }

  /**
   * Set the overlay (desktop popup) size, its height clamp, and/or the content
   * reading-width cap. Emits `widget:set-size`; the default UI applies it to the
   * shadow host's CSS vars (`--lastbot-widget-width` / `--lastbot-widget-height` /
   * `--lastbot-widget-max-height` / `--lastbot-content-max-width`), and
   * width/height/max-height changes animate. Pass a CSS length string (e.g.
   * "32rem", "500px"); `null` resets that key to its default; an omitted key
   * leaves the current value unchanged. `maxHeight` is the upper bound on height
   * — raise it to grow taller than the 600px default (a `height` above the cap
   * just renders at the cap). `contentMaxWidth` caps the conversation/composer
   * columns — set it to "none" so content fills a widened popup (e.g. to show
   * videos). `width`/`height`/`maxHeight` affect the desktop popup only (mobile
   * and fullscreen use their own layout); `contentMaxWidth` also caps the content
   * columns in fullscreen. All take effect at the `md` breakpoint and up.
   *
   *   core.setSize({ height: "720px", maxHeight: "760px" });     // taller, animated
   *   core.setSize({ width: "70rem", contentMaxWidth: "none" }); // wide, content fills
   *   core.setSize({ width: null, height: null, maxHeight: null, contentMaxWidth: null }); // reset
   */
  setSize(size: {
    width?: string | null;
    height?: string | null;
    maxHeight?: string | null;
    contentMaxWidth?: string | null;
  }): void {
    this.assertAlive();
    if (!isObject(size)) return;
    this.sizeRevision += 1;
    this.emitter.emit("widget:set-size", size);
  }

  /** @internal Default UI ownership reconciliation; not part of the embedder API. */
  _getSizeRevision(): number {
    return this.sizeRevision;
  }

  /**
   * Set the overlay panel's position offsets. Emits `widget:set-position`; the
   * default UI applies each edge to the shadow host's CSS vars
   * (`--lastbot-widget-offset-top` / `-right` / `-bottom` / `-left`). Pass a CSS
   * length (e.g. "24px", "1.5rem") or "auto"; `null` resets that edge to its
   * default; an omitted key leaves the current value unchanged. Because the
   * default UI writes the vars as inline host styles, an explicit offset applies
   * at every breakpoint. This moves the chat panel; the toggle button is
   * positioned via CSS variables only. Headless embedders can ignore.
   *
   *   core.setPosition({ left: "24px", right: "auto", bottom: "24px" }); // bottom-left
   *   core.setPosition({ top: null, right: null, bottom: null, left: null }); // reset
   */
  setPosition(pos: {
    top?: string | null;
    right?: string | null;
    bottom?: string | null;
    left?: string | null;
  }): void {
    this.assertAlive();
    if (!isObject(pos)) return;
    this.emitter.emit("widget:set-position", pos);
  }

  /**
   * Navigate the default UI to a page. Single entry point for embedder
   * navigation (v3 — replaces `startLogin()` / `openHistory()` /
   * `openWelcome()`):
   *
   *   core.showPage("history");                        // chat-history page
   *   core.showPage("landing");                        // welcome / landing page
   *   core.showPage("login", { returnTo: "history" }); // login flow
   *
   * Emits the matching `nav:*` event (`nav:history` / `nav:landing` /
   * `nav:login`); the default UI's ChatWidget listens and navigates, and
   * headless embedders can intercept the events or simply not call this.
   * `options.returnTo` applies to `"login"` only: the page to return to
   * after a successful login (`null` / omitted when no meaningful return
   * page is known).
   *
   * The `"conversation"` page is deliberately not navigable from here — it
   * needs thread context, which `openThread()` / `sendMessage()` /
   * `startNewThread()` already establish (and the default UI follows).
   *
   * Command ordering: calling this in the SAME tick as `open()` while the
   * panel was closed is unsupported — the `nav:*` event fires before the
   * panel's listener exists. `await core.open()` first.
   *
   * An unrecognized page (possible from untyped JS embedders) logs a
   * warning and emits nothing.
   */
  showPage(
    page: NavigablePage,
    options?: { returnTo?: string | null },
  ): void {
    this.assertAlive();
    switch (page) {
      case "login":
        this.emitter.emit("nav:login", { returnTo: options?.returnTo ?? null });
        return;
      case "history":
        this.emitter.emit("nav:history", {});
        return;
      case "landing":
        this.emitter.emit("nav:landing", {});
        return;
      default:
        console.warn(
          `[lastbot-chat] showPage: unknown page "${String(page)}" — expected "landing" | "history" | "login"`,
        );
    }
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    // Settle pending waits FIRST, before listener teardown: each reject
    // callback also runs its wait's cleanup (emitter disposer / state
    // unsubscribe / timers), which must happen while those registries still
    // exist in a consistent state. SPA unmounts must not leave hanging awaits.
    const pendingRejects = Array.from(this.pendingWaitRejects);
    this.pendingWaitRejects.clear();
    for (const rejectWait of pendingRejects) {
      rejectWait(
        new CoreWaitError("destroyed", "LastBotCore was destroyed while waiting"),
      );
    }
    this.abortController.abort();
    this.messageThreadChannel?.unsubscribe();
    this.messageThreadChannel = null;
    this.accountChannel?.unsubscribe();
    this.accountChannel = null;
    this.consumer?.disconnect();
    this.consumer = null;
    this.emitter.removeAllListeners();
    this.stateHandlers.clear();
  }


  /**
   * Force-start a new thread, sending `text` as its first message. Closes any currently active
   * thread first. Use this for the landing → conversation transition; for follow-ups within an
   * open thread use `sendMessage`.
   */
  async startNewThread(
    text: string,
    options?: { attachmentId?: string },
  ): Promise<{ threadId: number }> {
    this.assertAlive();
    this.requireToken("startNewThread");
    if (this.state.thread) {
      await this.closeThread(this.state.thread.id);
    }
    await this.sendMessage(text, options);
    const opened = this.state.thread;
    if (!opened) {
      throw new Error("startNewThread: sendMessage did not establish a thread");
    }
    return { threadId: opened.id };
  }

  /**
   * Send a message. If a thread is already open, the message goes through the existing channel
   * (fire-and-forget; the server broadcasts the created Message back via `message:sent`/`message:received`).
   * If no thread is open, this creates a new thread with the message as the first entry and subscribes.
   *
   * Resolves to `void` because messages dispatched through the channel are confirmed asynchronously via events,
   * not as a direct return value. (Reconcile with the plan's `Promise<Message>` signature in Phase 3 once
   * a correlation-id round-trip is added to the wire protocol.)
   */
  async sendMessage(
    text: string,
    options?: { attachmentId?: string },
  ): Promise<void> {
    this.assertAlive();
    this.requireToken("sendMessage");
    if (this.state.thread && this.messageThreadChannel) {
      // Remember the message contents so a server-side rejection delivered
      // asynchronously via the channel can surface message:failed with the
      // text that was attempted. Single-slot buffer: assumes one in-flight
      // message at a time, which matches today's UX.
      this.lastChannelMessage = text;
      const queued = this.messageThreadChannel.createMessage(
        text,
        options?.attachmentId ?? null,
      );
      if (!queued) {
        this.lastChannelMessage = null;
        this.emitter.emit("message:failed", {
          contents: text,
          error: "Message thread channel not connected",
        });
        throw new Error("sendMessage: channel not connected");
      }
      return;
    }
    const result = await createMessageThread(
      this.apiUrl,
      this.token!,
      this.config.widgetId,
      this.config.userEmail ?? "",
      text,
      options?.attachmentId ?? null,
      this.abortController.signal,
      this.config.homeUrl,
      this.config.skipIngest,
      this.config.hubIdentity,
    );
    if (!result.success) {
      this.emitter.emit("message:failed", {
        contents: text,
        error: "Failed to create message thread",
        status: result.status,
      });
      throw getApiError("sendMessage", result.status);
    }
    storageService.setContact(this.config.widgetId, result.messageThread.contact);
    this._setState(s => ({ ...s, contact: result.messageThread.contact }));
    this.subscribeToThread(result.messageThread);
    this.emitter.emit("thread:opened", { threadId: result.messageThread.id });
  }

  /**
   * Upload a file. Returns the attachment id which can be passed to `sendMessage` via
   * `options.attachmentId` to associate the upload with the next outgoing message.
   *
   * Progress reports are surfaced two ways:
   * - `options.onProgress(percent)` invoked synchronously during the XHR upload (percent
   *   is 0–100, decimal-precision).
   * - `attachment:uploaded` / `attachment:failed` events fire on completion / failure.
   *
   * Pass `options.signal` to allow the embedder to cancel an in-flight upload (e.g. the
   * user removed the attachment before send). The widget's own lifecycle (`destroy()`)
   * also cancels any in-flight upload.
   */
  async attachFile(
    file: File,
    options?: {
      onProgress?: (_percent: number) => void;
      signal?: AbortSignal;
    },
  ): Promise<{ id: string }> {
    this.assertAlive();
    this.requireToken("attachFile");
    const controller = uploadFile(
      this.apiUrl,
      this.token!,
      file,
      options?.onProgress ?? ((): void => undefined),
    );
    if (options?.signal) {
      options.signal.addEventListener("abort", () => controller.abort(), { once: true });
    }
    // Tie the upload to the core lifecycle so destroy() cancels in-flight uploads
    // even when the caller didn't pass an options.signal — matches the contract
    // every other network call in core honors via this.abortController.signal.
    this.abortController.signal.addEventListener(
      "abort",
      () => controller.abort(),
      { once: true },
    );
    const result = await controller.promise;
    if (!result.success) {
      const reasonType =
        (result.errorReason as ErrorReason | null | undefined)?.error_type ?? "unknown";
      this.emitter.emit("attachment:failed", {
        file: file.name,
        error: reasonType,
      });
      const err = new Error(`attachFile failed: ${reasonType}`, {
        cause: { errorReason: result.errorReason },
      });
      throw err;
    }
    this.emitter.emit("attachment:uploaded", { id: result.id, filename: file.name });
    return { id: result.id };
  }

  async openThread(threadId: number): Promise<{ threadId: number }> {
    this.assertAlive();
    this.requireToken("openThread");
    if (this.state.thread?.id === threadId) {
      return { threadId };
    }
    const result = await getMessageThread(
      this.apiUrl,
      this.token!,
      this.config.widgetId,
      threadId.toString(),
      this.abortController.signal,
    );
    if (!result.success) {
      throw getApiError("openThread", result.status);
    }
    this.subscribeToThread(result.messageThread);
    this.emitter.emit("thread:opened", { threadId });
    return { threadId };
  }

  /**
   * Stop watching the currently active thread. Note: the server-side thread is NOT closed by this call
   * — the server closes threads via task lifecycle events. This is "stop tracking" rather than "shut down".
   * The `rating` and `feedback` parameters are accepted for API symmetry with the plan and ignored for now.
   */
  closeThread(
    _threadId: number,
    _rating?: number,
    _feedback?: string,
  ): Promise<void> {
    this.assertAlive();
    const currentThreadId = this.state.thread?.id ?? null;
    this.messageThreadChannel?.unsubscribe();
    this.messageThreadChannel = null;
    this._setState(s => ({ ...s, thread: null }));
    if (currentThreadId !== null) {
      this.emitter.emit("thread:closed", { threadId: currentThreadId });
    }
    return Promise.resolve();
  }

  private subscribeToThread(thread: MessageThread): void {
    if (!this.consumer) {
      throw new Error("subscribeToThread requires init() to have completed");
    }
    this.messageThreadChannel?.unsubscribe();
    this.messageThreadChannel = new MessageThreadChannel(this.consumer);
    this.messageThreadChannel.subscribe(thread, {
      onConnected: () => this.handleThreadConnected(true),
      onDisconnected: () => this.handleThreadConnected(false),
      onMessageCreated: ({ message }) => this.handleMessageCreated(message),
      onMessageUpdated: ({ message }) => this.handleMessageUpdated(message),
      onMessageThreadUpdated: ({ messageThread }) =>
        this.handleMessageThreadUpdated(messageThread),
      onMessageError: ({ status }) => this.handleThreadMessageError(status),
      onTaskUpdated: ({ task }) => this.handleTaskUpdated(task),
      onTypingNotification: ({ typingNotification }) =>
        this.handleTypingNotification(typingNotification),
    });
    this._setState(s => ({
      ...s,
      thread: {
        id: thread.id,
        messages: thread.messages,
        closedAt: thread.task?.closed_at ?? null,
        rating: thread.rating,
        connected: false,
        assignedUser: thread.assigned_user,
      },
    }));
  }

  private handleThreadConnected(connected: boolean): void {
    this._setState(s => ({
      ...s,
      thread: s.thread ? { ...s.thread, connected } : s.thread,
    }));
  }

  private handleThreadMessageError(status: number): void {
    // Surface as both the typed message:failed event (preferred for embedders
    // who want a unified failure feed) and the generic error event (for
    // existing listeners). Uses lastChannelMessage to recover the contents
    // attempted; it may be `""` if a send-confirmation arrived between the
    // request and this error (rare), so we don't make absence a hard fail.
    const contents = this.lastChannelMessage ?? "";
    this.lastChannelMessage = null;
    this.emitter.emit("message:failed", {
      contents,
      error: `Message thread channel error (status ${status})`,
      status,
    });
    this.emitter.emit("error", {
      code: "thread.message-channel-error",
      message: `Message thread channel error (status ${status})`,
      details: { status },
    });
  }

  private handleMessageCreated(message: Message): void {
    this._setState(s => ({
      ...s,
      thread: s.thread
        ? { ...s.thread, messages: [...s.thread.messages, message] }
        : s.thread,
    }));
    if (message.role === "user") {
      // The user's message round-tripped through the server — clear the
      // single-slot tracker so a later channel error doesn't attribute itself
      // to a successfully-delivered message.
      if (message.contents === this.lastChannelMessage) {
        this.lastChannelMessage = null;
      }
      this.emitter.emit("message:sent", message);
    } else {
      this.emitter.emit("message:received", message);
    }
  }

  private handleMessageUpdated(message: Message): void {
    this._setState(s => ({
      ...s,
      thread: s.thread
        ? {
            ...s.thread,
            messages: s.thread.messages.map(m =>
              m.id === message.id ? message : m,
            ),
          }
        : s.thread,
    }));
    this.emitter.emit("message:updated", message);
  }

  private handleMessageThreadUpdated(updated: MessageThread): void {
    this._setState(s => ({
      ...s,
      thread: s.thread
        ? {
            ...s.thread,
            rating: updated.rating ?? s.thread.rating,
            assignedUser: updated.assigned_user,
          }
        : s.thread,
    }));
  }

  private handleTaskUpdated(task: Task): void {
    if (!task.closed_at) return;
    const currentThreadId = this.state.thread?.id ?? null;
    this._setState(s => ({
      ...s,
      thread: s.thread ? { ...s.thread, closedAt: task.closed_at } : s.thread,
    }));
    if (currentThreadId !== null) {
      this.emitter.emit("thread:closed", { threadId: currentThreadId });
    }
  }

  private handleTypingNotification(notification: TypingNotification): void {
    // ActionCable echoes back the widget's own typing notifications when the
    // backend fans the channel out to every subscriber. Ignore widget-sourced
    // events so the visitor's keystrokes don't flip the "agent is typing"
    // indicator on for themselves (or clobber a real agent's state).
    if (notification.source === "widget") return;

    const isTyping = notification.typing_state === TypingState.TYPING;
    this._setState(s => ({
      ...s,
      agents: { ...s.agents, typing: isTyping },
    }));
    this.emitter.emit("agent:typing", { isTyping });
  }

  /** @internal — used by ConversationPage during the Phase 1 migration. Not part of the public API. */
  _sendTypingNotification(state: TypingState): void {
    this.messageThreadChannel?.sendTypingNotification(state);
  }

  /**
   * Request LiveKit credentials for a voice session attached to the currently active thread.
   * Returns `{ url, token }` for the embedder (or default UI's ChatFormVoice) to connect to
   * LiveKit themselves. The core does not own the audio session — only the credential issuance.
   * Updates `state.voice.state` through 'connecting' → 'streaming' and emits `voice:state`.
   *
   * Throws if no thread is active. Voice always attaches to an existing conversation.
   */
  async startVoice(): Promise<VoiceSession> {
    this.assertAlive();
    this.requireToken("startVoice");
    if (!this.state.thread) {
      throw new Error("startVoice requires an active thread");
    }
    this.setVoiceState("connecting");
    const result = await createVoiceSession(
      this.apiUrl,
      this.token!,
      this.config.widgetId,
      this.state.thread.id.toString(),
      this.abortController.signal,
    );
    if (this.destroyed) {
      // Caller already torn down; don't mutate state further.
      throw new Error("startVoice: core destroyed during session creation");
    }
    if (!result.success) {
      this.setVoiceState("error");
      throw getApiError("startVoice", result.status);
    }
    this.setVoiceState("streaming");
    return result.voiceSession;
  }

  /**
   * Reset voice state back to 'idle'. The actual LiveKit room disconnection happens in the
   * UI layer (or embedder code) that owns the audio session; this method just clears the
   * core's voice state and emits `voice:state`.
   */
  endVoice(): Promise<void> {
    this.assertAlive();
    this.setVoiceState("idle");
    this._setState(s => ({ ...s, voice: { state: "idle", transcript: "" } }));
    return Promise.resolve();
  }

  private setVoiceState(state: LastBotState["voice"]["state"]): void {
    if (this.state.voice.state === state) return;
    this._setState(s => ({ ...s, voice: { ...s.voice, state } }));
    this.emitter.emit("voice:state", state);
  }

  async loginWithEmail(email: string): Promise<Contact> {
    this.assertAlive();
    this.requireToken("loginWithEmail");
    const result = await createLoginCode(
      this.apiUrl,
      this.token!,
      this.config.widgetId,
      email,
      this.abortController.signal,
    );
    if (!result.success) {
      this.emitter.emit("auth:failed", {
        error: `loginWithEmail failed (status ${result.status ?? "unknown"})`,
      });
      throw getApiError("loginWithEmail", result.status);
    }
    this.loginEmail = email;
    storageService.setContact(this.config.widgetId, result.contact);
    this._setState(s => ({ ...s, contact: result.contact }));
    return result.contact;
  }

  async submitLoginCode(code: string, email?: string): Promise<Contact> {
    this.assertAlive();
    this.requireToken("submitLoginCode");
    // Prefer the caller-supplied email — page-level callers know the
    // email from page params (e.g. EnterCodePage's `params.email`) and
    // can verify even after a reload that clears in-memory loginEmail.
    // Fall back to the loginWithEmail()-captured value for headless
    // embedders who orchestrate the flow programmatically.
    const effectiveEmail = email ?? this.loginEmail;
    if (!effectiveEmail) {
      throw new Error(
        "submitLoginCode requires an email — pass it as the second argument or call loginWithEmail() first",
      );
    }
    const result = await verifyLoginCode(
      this.apiUrl,
      this.token!,
      this.config.widgetId,
      effectiveEmail,
      code,
      this.abortController.signal,
    );
    if (!result.success) {
      this.emitter.emit("auth:failed", {
        error: `submitLoginCode failed (status ${result.status ?? "unknown"})`,
      });
      throw getApiError("submitLoginCode", result.status);
    }
    storageService.setContact(this.config.widgetId, result.contact);
    this._setState(s => ({ ...s, contact: result.contact }));
    this.emitter.emit("auth:succeeded", { contact: result.contact });
    return result.contact;
  }

  private requireToken(method: string): void {
    if (!this.token) {
      throw new Error(`LastBotCore.${method} requires init() to have completed`);
    }
  }

  requestSupportAgent(): Promise<void> {
    return Promise.reject(getNotImplementedError("requestSupportAgent"));
  }

  async getThreadHistory(threadId: number): Promise<Message[]> {
    this.assertAlive();
    this.requireToken("getThreadHistory");
    const result = await getMessageThread(
      this.apiUrl,
      this.token!,
      this.config.widgetId,
      threadId.toString(),
      this.abortController.signal,
    );
    if (!result.success) {
      throw getApiError("getThreadHistory", result.status);
    }
    return result.messageThread.messages;
  }

  /**
   * Fetch search suggestions for the customer-inquiry "ask anything" form. Returns up to N
   * suggestion strings filtered to ≤140 chars each.
   */
  async getSearchSuggestions(query: string, signal?: AbortSignal): Promise<string[]> {
    this.assertAlive();
    this.requireToken("getSearchSuggestions");
    const result = await getSearchSuggestions(
      this.apiUrl,
      this.token!,
      query,
      signal,
    );
    if (!result.success) {
      throw getApiError("getSearchSuggestions", result.status);
    }
    return result.searchSuggestions;
  }

  /**
   * Build a click-tracking URL for a link inside a message. Synchronous (no network call) —
   * the backend redirects through this URL to log the click + the originating thread.
   */
  getLinkTrackingUrl(threadId: number, url: string): string {
    this.assertAlive();
    this.requireToken("getLinkTrackingUrl");
    return getLinkTrackingUrl(
      this.apiUrl,
      this.token!,
      this.config.widgetId,
      threadId.toString(),
      url,
    );
  }

  async getThreadList(): Promise<MessageThread[]> {
    this.assertAlive();
    this.requireToken("getThreadList");
    const result = await getMessageThreads(
      this.apiUrl,
      this.token!,
      this.config.widgetId,
      this.abortController.signal,
    );
    if (!result.success) {
      throw getApiError("getThreadList", result.status);
    }
    return result.messageThreads;
  }

  /** @internal — used by services migrated into the core. Not part of the public API. */
  _setState(updater: (state: LastBotState) => LastBotState): void {
    this.assertAlive();
    this.state = updater(this.state);
    for (const handler of this.stateHandlers) {
      handler(this.state);
    }
  }

  /** @internal — used by services migrated into the core. Not part of the public API. */
  _emit<K extends CoreEventName>(
    event: K,
    payload: Parameters<CoreEventHandler<K>>[0],
  ): void {
    this.assertAlive();
    this.emitter.emit(event, payload);
  }

  private assertAlive(): void {
    if (this.destroyed) {
      throw new Error("LastBotCore has been destroyed");
    }
  }
}
